import React, { useState } from "react";
import { generateMetadataSuggestions, GroqSuggestion } from "../../services/groqService";

interface Props {
  groqApiKey: string;
  currentTitle: string;
  currentDescription: string;
  niche: string;
}

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: "16px",
  padding: "24px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  border: "1px solid #f0f0f0",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
  marginBottom: "16px",
  fontSize: "14px",
};

const MetadataOptimizer: React.FC<Props> = ({ groqApiKey, currentTitle, currentDescription, niche }) => {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<GroqSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOptimize = async () => {
    if (!groqApiKey) {
      setError("Please provide a Groq API Key to use this feature.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await generateMetadataSuggestions(groqApiKey, currentTitle, currentDescription, niche);
      setSuggestion(result);
    } catch (err: any) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: "0 0 20px 0", color: "#1a1a1a", fontSize: "20px" }}>AI Metadata Architect</h3>
      
      <div style={{ marginBottom: "24px" }}>
        <p style={{ fontSize: "12px", color: "#666", marginBottom: "4px", fontWeight: 600 }}>TARGET NICHE</p>
        <div style={{ fontSize: "14px", color: "#333", background: "#f9f9f9", padding: "10px", borderRadius: "6px" }}>
          {niche || "General Content"}
        </div>
      </div>

      {!suggestion ? (
        <button
          onClick={handleOptimize}
          disabled={loading}
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
            color: "#fff",
            border: "none",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            transition: "transform 0.2s",
          }}
        >
          {loading ? "Analyzing with Groq AI..." : "Generate Viral Metadata"}
        </button>
      ) : (
        <div style={{ animation: "fadeIn 0.5s ease" }}>
          <div style={{ background: "#f5f3ff", padding: "16px", borderRadius: "12px", marginBottom: "16px", borderLeft: "4px solid #8b5cf6" }}>
            <p style={{ fontSize: "12px", color: "#7c3aed", fontWeight: 700, margin: "0 0 8px 0" }}>SUGGESTED TITLE (High CTR)</p>
            <p style={{ fontSize: "16px", color: "#1e1b4b", margin: 0, fontWeight: 600 }}>{suggestion.title}</p>
          </div>

          <div style={{ background: "#f0fdf4", padding: "16px", borderRadius: "12px", marginBottom: "16px", borderLeft: "4px solid #22c55e" }}>
            <p style={{ fontSize: "12px", color: "#16a34a", fontWeight: 700, margin: "0 0 8px 0" }}>SEO OPTIMIZED DESCRIPTION</p>
            <p style={{ fontSize: "14px", color: "#14532d", margin: 0, whiteSpace: "pre-wrap" }}>{suggestion.description}</p>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <p style={{ fontSize: "12px", color: "#666", fontWeight: 700, margin: "0 0 8px 0" }}>TAG STRATEGY</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {suggestion.tags.map(tag => (
                <span key={tag} style={{ background: "#eff6ff", color: "#1d4ed8", padding: "4px 10px", borderRadius: "999px", fontSize: "12px" }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div style={{ background: "#fffbeb", padding: "12px", borderRadius: "8px", border: "1px dashed #f59e0b" }}>
            <p style={{ fontSize: "11px", color: "#92400e", margin: 0 }}><strong>Strategy Note:</strong> {suggestion.reasoning}</p>
          </div>

          <button
            onClick={() => setSuggestion(null)}
            style={{ marginTop: "20px", background: "none", border: "none", color: "#666", textDecoration: "underline", cursor: "pointer", fontSize: "13px" }}
          >
            Start Over
          </button>
        </div>
      )}

      {error && <p style={{ color: "#dc2626", marginTop: "16px", fontSize: "13px" }}>{error}</p>}
    </div>
  );
};

export default MetadataOptimizer;
